import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { GatewayInstance, Payment } from '@vsolv/packages/payments/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { PaymentService } from '../../services';

export interface RetryPaymentDialogLabels {
  title: string;
  subtitle: string;
  action: string;
  success: string;
}

const DEFAULT_LABELS: RetryPaymentDialogLabels = {
  title: 'Retry payment',
  subtitle: 'Update the billing information and retry this payment.',
  action: 'Retry payment',
  success: 'The payment has been paid successfully.',
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-retry-payment-dialog',
  templateUrl: './retry-payment.dialog.html',
  standalone: false,
})
export class RetryPaymentDialog {
  private toastSvc = inject(ToastService);
  private paymentSvc = inject(PaymentService);

  @ViewChild(DialogComponent) private dialog?: DialogComponent;

  @Output() readonly saved = new EventEmitter<Payment.RetryPaymentResponse>();
  /** Emitted after a declined or failed attempt, which can leave the payment FAILED server-side. */
  @Output() readonly failed = new EventEmitter<void>();

  /** Wording for flows that reuse this dialog, such as a first collection of a Pay Later balance. */
  @Input() labels?: RetryPaymentDialogLabels;

  protected get text(): RetryPaymentDialogLabels {
    return { ...DEFAULT_LABELS, ...this.labels };
  }

  protected _payment?: Payment.Model;
  @Input() set payment(payment: Payment.Model | undefined) {
    this._payment = payment;
    this.reset();
  }
  get payment() {
    return this._payment;
  }

  protected get gatewayInstanceId(): string | null {
    if (!this._payment) return null;
    if (this._payment.gatewayInstanceId) return this._payment.gatewayInstanceId;
    return this._payment.gatewayRoutingReason === GatewayInstance.RoutingReason.LEGACY_COMPATIBILITY
      ? GatewayInstance.LEGACY_AUTHORIZE_NET_INSTANCE_ID
      : null;
  }

  protected paymentMethodControl = new FormControl(
    { value: this.payment?.paymentMethodId ?? null, disabled: false },
    Validators.required
  );

  protected saving = false;

  open() {
    this.reset();
    this.dialog?.open();
  }

  close() {
    this.dialog?.close();
  }

  protected async save() {
    this.saving = true;

    try {
      const paymentId = this._payment?.id;
      const paymentMethodId = this.paymentMethodControl.value;
      if (!paymentId || !paymentMethodId) throw new Error('Unable to retry payment.');

      const result = await this.paymentSvc.retryPayment(paymentId, paymentMethodId);
      this.saved.emit(result);

      this.toastSvc.show({
        type: 'success',
        title: 'Payment successfully completed',
        text: this.text.success,
      });

      this.close();
    } catch (err) {
      console.error(err);
      this.failed.emit();
      const errorText = this.getErrorMessage(err);
      this.toastSvc.show({
        title: 'Unable to complete payment',
        type: errorText === 'Make sure to select a different payment method or add a new one.' ? 'warning' : 'error',
        text: errorText,
      });
    } finally {
      this.saving = false;
    }
  }

  private reset() {
    this.paymentMethodControl.reset({ value: this.payment?.paymentMethodId ?? null, disabled: false });
  }

  private getErrorMessage(err: unknown): string {
    const responseMessage = (err as { error?: { message?: unknown } })?.error?.message;
    if (Array.isArray(responseMessage)) {
      const firstMessage = responseMessage.find(message => typeof message === 'string');
      if (firstMessage) return firstMessage;
    }

    if (typeof responseMessage === 'string' && responseMessage.trim()) {
      return responseMessage;
    }

    if (err instanceof Error && err.message) {
      return err.message;
    }

    return 'Unknown Error';
  }
}
